import { action, makeObservable, observable } from "mobx";
import { ILog } from "@ihr-radioedit/inferno-core";
import { isWindowDefined } from "../utilities/window";

const log = ILog.logger("StorePrivacy");

interface PrivacyConfig {
  host: string;
  iglooUrl: string;
  oneTrustId?: string;
  autoBlock?: boolean;
  ampConfig?: {
    profileId: string;
    sessionId: string;
  };
  user?: {
    profileId: number;
    name?: string;
    email?: string;
  };
}

interface PrivacySDK {
  initialize: (config: PrivacyConfig) => Promise<void>;
  toggleModal: () => void;
  getOptOut: () => Promise<boolean>;
  subscribe: (handlers: {
    initialize?: (isOptedOut: boolean) => void;
    setOptOut?: (isOptedOut: boolean) => void;
    all?: (method: string, result: unknown) => void;
    catch?: (method: string, error: unknown) => void;
  }) => () => void;
}

const getSDK = (): PrivacySDK | undefined => {
  if (!isWindowDefined()) return undefined;
  return (window as Window & { iHeartPrivacy?: PrivacySDK }).iHeartPrivacy;
};

export class StorePrivacy {
  // null = not yet resolved, true = resolved successfully, false = resolved with error
  @observable initialized: boolean | null = null;
  @observable optedOut = true;
  @observable enabled: boolean;

  constructor(enabled = true) {
    this.enabled = enabled;
    makeObservable(this);
  }

  @action
  setInitialized = (value: boolean | null) => {
    this.initialized = value;
  };

  @action
  setOptedOut = (value: boolean) => {
    this.optedOut = value;
  };

  toggleModal = () => {
    if (!isWindowDefined()) return;
    getSDK()?.toggleModal();
  };

  initialize = async (config: PrivacyConfig) => {
    if (!isWindowDefined()) return;

    const sdk = getSDK();
    if (!sdk) {
      log.error("Privacy SDK not found on window");
      this.setInitialized(false);
      return;
    }

    sdk.subscribe({
      initialize: (isOptedOut: boolean) => {
        this.setOptedOut(isOptedOut);
        this.setInitialized(true);
      },
      setOptOut: (isOptedOut: boolean) => {
        this.setOptedOut(isOptedOut);
      },
    });

    try {
      await sdk.initialize(config);
    } catch (err) {
      log.error("Error initializing Privacy SDK", err);
      this.setInitialized(false);
      throw err;
    }
  };
}
